import { ArrowLeftIcon, PlusIcon, SearchIcon, UsersRoundIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Pagina } from '@/components/Pagina'
import { Encabezado } from '@/components/Encabezado'
import { Boton } from '@/components/ui/Boton'
import {
  MenuContenido,
  MenuDesplegable,
  MenuDisparador,
  MenuItem,
} from '@/components/ui/MenuDesplegable'
import { useSesionActiva } from '@/features/auth/RutaProtegida'
import { leerUltimoProyecto } from '@/features/auth/useSesion'
import { useEsEscritorio } from '@/lib/useEsEscritorio'
import { useClientes, useDetalleProyecto, useProyectosDeCliente } from './api'
import { ColumnaClientes } from './ColumnaClientes'
import { ColumnaProyectos } from './ColumnaProyectos'
import { DetalleProyecto } from './DetalleProyecto'
import { ModalEquipo } from './ModalEquipo'

type Nivel = 'clientes' | 'proyectos' | 'detalle'

/**
 * P03 · Administración (§8.6). Tres columnas encadenadas en escritorio;
 * en celular, navegación en profundidad cliente → proyectos → detalle con
 * el botón flotante "+" contextual. La selección vive en la URL
 * (?cliente=…&proyecto=…) para que "atrás" funcione como se espera.
 */
export function Administracion() {
  const { usuario, proyectos: permitidos } = useSesionActiva()
  const escritorio = useEsEscritorio()
  const [params, setParams] = useSearchParams()
  const clienteId = params.get('cliente')
  const proyectoId = params.get('proyecto')

  const [busqueda, setBusqueda] = useState('')
  const [mostrarInactivos, setMostrarInactivos] = useState(false)
  const [equipoAbierto, setEquipoAbierto] = useState(false)
  const [flotante, setFlotante] = useState<
    'cliente' | 'proyecto' | 'pilar' | 'invitar' | null
  >(null)

  const clientes = useClientes()
  const proyectos = useProyectosDeCliente(clienteId)
  const detalle = useDetalleProyecto(proyectoId)

  const cliente = clientes.data?.find((c) => c.id === clienteId) ?? null
  const nivel: Nivel = proyectoId ? 'detalle' : clienteId ? 'proyectos' : 'clientes'

  const elegir = (
    cambios: { cliente?: string | null; proyecto?: string | null },
    reemplazar = false,
  ) => {
    const siguiente = new URLSearchParams(params)
    if (cambios.cliente !== undefined) {
      if (cambios.cliente) siguiente.set('cliente', cambios.cliente)
      else siguiente.delete('cliente')
    }
    if (cambios.proyecto !== undefined) {
      if (cambios.proyecto) siguiente.set('proyecto', cambios.proyecto)
      else siguiente.delete('proyecto')
    }
    setParams(siguiente, { replace: reemplazar })
  }

  // Escritorio: las tres columnas siempre muestran algo. Se preselecciona el
  // primer cliente activo y su primer proyecto activo; en celular no, porque
  // cada nivel es una pantalla.
  useEffect(() => {
    if (!escritorio || !clientes.data) return
    if (!clienteId) {
      const primero = clientes.data.find((c) => c.activo) ?? clientes.data[0]
      if (primero) elegir({ cliente: primero.id }, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sólo cuando llega la lista
  }, [escritorio, clientes.data, clienteId])

  useEffect(() => {
    if (!escritorio || !clienteId || !proyectos.data) return
    if (!proyectoId || !proyectos.data.some((p) => p.id === proyectoId)) {
      const primero = proyectos.data.find((p) => p.activo) ?? null
      elegir({ proyecto: primero?.id ?? null }, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sólo cuando llega la lista
  }, [escritorio, clienteId, proyectos.data, proyectoId])

  const ultimo = leerUltimoProyecto()
  const volverA =
    ultimo && permitidos.some((p) => p.id === ultimo) ? ultimo : permitidos[0]?.id

  const buscador = (
    <label className="relative block">
      <span className="sr-only">Buscar cliente, proyecto o persona</span>
      <SearchIcon
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-suave"
        aria-hidden
      />
      <input
        type="search"
        value={busqueda}
        onChange={(e) => {
          setBusqueda(e.target.value)
        }}
        placeholder="Buscar cliente, proyecto o persona"
        className="h-10 w-full rounded-full border border-borde bg-tarjeta pr-4 pl-9 text-base placeholder:text-tinta-suave/70 sm:text-sm focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-tinta lg:w-72"
      />
    </label>
  )

  const tituloCelular =
    nivel === 'detalle' && detalle.data
      ? detalle.data.proyecto.nombre
      : nivel === 'proyectos' && cliente
        ? cliente.nombre
        : 'Administración'

  return (
    <>
      <Encabezado
        usuario={usuario}
        titulo={
          <span data-titulo-vista>{escritorio ? 'Administración' : tituloCelular}</span>
        }
        antesDelLogo={
          nivel !== 'clientes' && !escritorio ? (
            <Boton
              variante="sutil"
              tamano="icono"
              aria-label="Volver"
              className="-ml-2"
              onClick={() => {
                elegir(nivel === 'detalle' ? { proyecto: null } : { cliente: null })
              }}
            >
              <ArrowLeftIcon className="size-5" aria-hidden />
            </Boton>
          ) : undefined
        }
        centro={
          <>
            {volverA && (
              <Link
                to={`/proyecto/${volverA}`}
                className="hidden items-center gap-1.5 text-sm text-tinta-suave hover:text-tinta lg:inline-flex"
              >
                <ArrowLeftIcon className="size-4" aria-hidden />
                Volver al proyecto
              </Link>
            )}
            <div className="hidden lg:block">{buscador}</div>
          </>
        }
        menuExtra={
          <MenuItem
            onSelect={() => {
              setEquipoAbierto(true)
            }}
          >
            <UsersRoundIcon className="size-4" aria-hidden />
            Equipo RASTRO
          </MenuItem>
        }
      />

      <Pagina as="main" className="py-4 pb-24 lg:py-6">
        <div className="mb-4 lg:hidden">{nivel === 'clientes' && buscador}</div>

        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.5fr)]">
          {(escritorio || nivel === 'clientes') && (
            <ColumnaClientes
              clientes={clientes.data ?? []}
              cargando={clientes.isPending}
              seleccionado={clienteId}
              alElegir={(id) => {
                elegir({ cliente: id, proyecto: null })
              }}
              busqueda={busqueda}
              mostrarInactivos={mostrarInactivos}
              alMostrarInactivos={setMostrarInactivos}
              abrirNuevo={flotante === 'cliente'}
              alCerrarNuevo={() => {
                setFlotante(null)
              }}
            />
          )}

          {(escritorio || nivel === 'proyectos') && (
            <ColumnaProyectos
              cliente={cliente}
              proyectos={proyectos.data ?? []}
              cargando={proyectos.isPending && Boolean(clienteId)}
              seleccionado={proyectoId}
              alElegir={(id) => {
                elegir({ proyecto: id })
              }}
              busqueda={busqueda}
              mostrarInactivos={mostrarInactivos}
              abrirNuevo={flotante === 'proyecto'}
              alCerrarNuevo={() => {
                setFlotante(null)
              }}
            />
          )}

          {(escritorio || nivel === 'detalle') && (
            <DetalleProyecto
              detalle={detalle.data ?? null}
              cargando={detalle.isPending && Boolean(proyectoId)}
              proyectosDelCliente={(proyectos.data ?? []).filter((p) => p.activo)}
              busqueda={busqueda}
              abrirNuevoPilar={flotante === 'pilar'}
              alCerrarNuevoPilar={() => {
                setFlotante(null)
              }}
              abrirInvitar={flotante === 'invitar'}
              alCerrarInvitar={() => {
                setFlotante(null)
              }}
            />
          )}
        </div>
      </Pagina>

      {/* Botón flotante contextual: sólo en celular (§8.6). */}
      {!escritorio && (
        <div className="fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30">
          {nivel === 'detalle' ? (
            <MenuDesplegable>
              <MenuDisparador asChild>
                <Boton
                  tamano="icono"
                  className="size-14 shadow-tarjeta"
                  aria-label="Agregar"
                >
                  <PlusIcon className="size-6" aria-hidden />
                </Boton>
              </MenuDisparador>
              <MenuContenido side="top">
                <MenuItem
                  onSelect={() => {
                    setFlotante('pilar')
                  }}
                >
                  Nuevo pilar
                </MenuItem>
                <MenuItem
                  onSelect={() => {
                    setFlotante('invitar')
                  }}
                >
                  Invitar persona
                </MenuItem>
              </MenuContenido>
            </MenuDesplegable>
          ) : (
            <Boton
              tamano="icono"
              className="size-14 shadow-tarjeta"
              aria-label={nivel === 'clientes' ? 'Nuevo cliente' : 'Nuevo proyecto'}
              onClick={() => {
                setFlotante(nivel === 'clientes' ? 'cliente' : 'proyecto')
              }}
            >
              <PlusIcon className="size-6" aria-hidden />
            </Boton>
          )}
        </div>
      )}

      <ModalEquipo
        abierto={equipoAbierto}
        alCerrar={() => {
          setEquipoAbierto(false)
        }}
      />
    </>
  )
}
